import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { MotionButton, listItem } from "@/lib/motion";
import { toast } from "sonner";
import { PauseCircle, Play, Loader2 } from "lucide-react";

export async function resumeCandidate(id) {
  try { await api.post(`/candidates/${id}/proctor/resume`); toast.success("They can continue their test now"); return true; }
  catch (e) { toast.error(e.response?.data?.detail || "Could not resume"); return false; }
}

function PausedRow({ p, onResumed }) {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); if (await resumeCandidate(p.id)) onResumed(p.id); setBusy(false); };
  return (
    <motion.div layout variants={listItem} initial="hidden" animate="show" exit="exit" className="flex items-center gap-3 rounded-lg border border-amber-400/25 bg-amber-500/[0.06] px-3 py-2.5" data-testid={`paused-row-${p.id}`}>
      <PauseCircle className="h-4 w-4 text-amber-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <Link to={`/recruiter/candidate/${p.id}?tab=proctoring`} className="text-sm font-medium hover:underline">{p.name || "Candidate"}</Link>
        <div className="text-xs text-muted-foreground truncate">{p.job_title} · {p.warnings} confirmed warning{p.warnings === 1 ? "" : "s"}{p.last ? ` · last: ${p.last}` : ""}</div>
      </div>
      <MotionButton whileTap={{ scale: 0.97 }} size="sm" onClick={go} disabled={busy} className="gap-1.5 shrink-0" data-testid={`let-continue-${p.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Let them continue</MotionButton>
    </motion.div>
  );
}

// Live list of candidates whose test is paused under its monitoring rules.
export function PausedCandidates() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/proctor/paused").then(({ data }) => { if (alive) setRows(data.paused || []); }).catch(() => {});
    tick();
    const t = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!rows.length) return null;
  return (
    <section className="hs-card p-4 space-y-2.5" data-testid="paused-candidates">
      <div className="flex items-center gap-2 text-sm font-semibold"><span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" /> Paused tests waiting for you <span className="text-xs font-normal text-muted-foreground">· their timers are stopped</span></div>
      <AnimatePresence initial={false}>{rows.map((p) => <PausedRow key={p.id} p={p} onResumed={(id) => setRows((r) => r.filter((x) => x.id !== id))} />)}</AnimatePresence>
    </section>
  );
}
